/**
 * Article Generation Badge Component
 *
 * Shows live generation status on announcement cards in dashboard/list views.
 * Displays phase-specific icons and labels with animations.
 */

import { Sparkles, Search, Wand2, ShieldCheck, Loader2 } from 'lucide-react';
import type { GlobalGenerationState } from '../hooks/useGlobalArticleGeneration.js';

interface ArticleGenerationBadgeProps {
  generationState: GlobalGenerationState | null;
  compact?: boolean;
}

const PHASE_CONFIG = {
  generating: {
    icon: Sparkles,
    label: 'Generating',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
  },
  fact_checking: {
    icon: Search,
    label: 'Fact-checking',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
  },
  polishing: {
    icon: Wand2,
    label: 'Polishing',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
  },
  language_review: {
    icon: ShieldCheck,
    label: 'Voice Review',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
};

export function ArticleGenerationBadge({ generationState, compact = false }: ArticleGenerationBadgeProps) {
  if (!generationState) return null;

  const config = PHASE_CONFIG[generationState.currentPhase];
  const Icon = config.icon;

  if (compact) {
    // Compact version for list views
    return (
      <div
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.borderColor} ${config.bgColor} ${config.color}`}
      >
        <Icon className="h-3 w-3 animate-pulse" />
        <span>{config.label}</span>
        <Loader2 className="h-3 w-3 animate-spin" />
      </div>
    );
  }

  // Full version for detail views
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${config.borderColor} ${config.bgColor}`}
    >
      <Icon className={`h-4 w-4 ${config.color} animate-pulse`} />
      <div className="flex flex-col">
        <span className={`text-xs font-semibold ${config.color}`}>
          {generationState.articlesGenerating} article{generationState.articlesGenerating > 1 ? 's' : ''} generating
        </span>
        <span className="text-xs text-grey-600">{generationState.phaseLabel}</span>
      </div>
      <Loader2 className={`h-4 w-4 ${config.color} animate-spin`} />
    </div>
  );
}
